import axios from 'axios';
import TelemetryService from 'src/services/telemetry';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

export function useTraceCycle({ props, emit } = {}) {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();

  // Refs to external DOM (wire up from the host)
  const videoEl = ref(null);
  const boundingBoxesEl = ref(null);

  // State (parity with RecordedInference.vue)
  const isGoodCycle = ref(false);
  const frame = ref(0);
  const currentStepList = ref([]);
  const currentCycle = ref(1);
  const filePreds = ref(null);
  const stepsStartPoints = ref({});
  const stepsTime = ref({});
  const substepList = ref([]);
  const backgroundTime = ref(0);
  const missedSteps = ref([]);
  const detectedSteps = ref({});
  const isPredictionFileNotExist = ref(false);
  const processDetails = ref({});
  const showVideoControls = ref(false);
  const propagatedSteps = ref([]);
  const traceCycleData = ref(null);
  const currentVideoTime = ref(0);
  const missedStepsFromTelemetry = ref([]);
  const cycleTime = ref(0);
  const taskId = ref(null);
  const historyId = ref(null);
  const animationFrameId = ref(0);
  const substepMetrics = ref({}); // Track VA, NVA, and Total time for each substep
  const precomputedMetrics = ref({}); // Lookup table: frame -> metrics snapshot
  // Per-frame cycle meta: filePreds[frame].meta is sparse + diff-based, so we
  // fold forward into snapshots ({ frame, state }) once preds load.
  const metaSnapshots = ref([]);
  const metaKeys = ref([]);

  // Client like UI

  // Getters from store
  const negativeSteps = computed(() => store.getters['negativeSteps']);
  const optionalSteps = computed(() => store.getters['optionalSteps']);
  const taskStepsCount = computed(() => store.getters['taskStepsCount']);
  const isFetchingTaskDetails = computed(
    () => store.getters['isFetchingTaskDetails']
  );
  const stepsTranslationMapping = computed(
    () => store.getters['stepsTranslationMapping']
  );
  const role = computed(() => store.getters['role']);

  // Computed (parity)
  const cycleType = computed(() =>
    !traceCycleData.value?.is_good_cycle ? 'Good' : 'Bad'
  );
  const isMetaExist = computed(
    () =>
      traceCycleData.value?.metadata &&
      Object.keys(traceCycleData.value?.metadata).length
  );

  const currentStepsText = computed(() => {
    const currentSteps = filePreds.value?.[frame.value]?.pred;
    if (!currentSteps) return [];
    if (currentSteps[0] === taskStepsCount.value) return ['● Background'];
    return currentSteps.map(
      (substep) => `● ${substepList.value[substep]?.name}`
    );
  });

  const nextStepText = computed(() => {
    const nextStep = filePreds.value?.[frame.value]?.next_step;
    if (nextStep === undefined || nextStep === null) return null;
    return `● ${substepList.value[nextStep]?.name}`;
  });

  const potentialIssuesText = computed(() => {
    const missedStep = filePreds.value?.[frame.value]?.missed_steps;
    if (!missedStep) return [];
    return missedStep.map(
      (substep) => `● ${substepList.value[substep]?.name}`
    );
  });

  // Per-frame FPS reported by the preds file at the current frame.
  const currentFps = computed(() => {
    const f = filePreds.value?.[frame.value]?.fps;
    return Number.isFinite(f) ? f : null;
  });

  // Snapshot of cumulative meta for the latest snapshot frame <= current frame.
  // Returns null before the first meta frame so the UI can show a placeholder.
  const currentMeta = computed(() => {
    const snaps = metaSnapshots.value;
    if (!snaps.length) return null;
    let chosen = null;
    for (const s of snaps) {
      if (s.frame <= frame.value) chosen = s;
      else break;
    }
    return chosen ? chosen.state : null;
  });

  // Data-driven station meta: one box per key seen in any frame's meta blob.
  // Labels are titlecased from the key name; no per-station config.
  const stationMeta = computed(() => {
    const keys = metaKeys.value;
    if (!keys.length) return [];
    const state = currentMeta.value;
    return keys.map((k) => {
      const label = String(k)
        .replace(/[_\s]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      // Fold-forward keeps the last seen value; '–' only before any snapshot.
      if (!state || state[k] === undefined || state[k] === null) {
        return { label, value: '–' };
      }
      return { label, value: state[k] };
    });
  });

  const stepsCompleted = computed(() => {
    return Object.entries(stepsTime.value).filter(
      ([step, time]) =>
        time > 0 && !missedStepsFromTelemetry.value.includes(step)
    ).length;
  });

  // Helpers
  function setStepsStartTime(list = substepList.value) {
    stepsTime.value = list.reduce((acc, el) => {
      acc[el.substepIndex] = 0;
      return acc;
    }, {});
  }

  function setStepStartPoint(preds) {
    const start = {};
    Object.entries(preds).forEach(([frameKey, value]) => {
      value.pred.forEach((k) => {
        if (!(k in start)) start[k] = frameKey;
      });
    });
    stepsStartPoints.value = start;
  }

  function addStepInDetectedStepsDict(step) {
    const tmp = { ...detectedSteps.value };
    if (missedStepsFromTelemetry.value?.includes(step)) tmp[step] = false;
    else tmp[step] = true;
    detectedSteps.value = tmp;
  }

  function updateSubstepTimes(f = frame.value) {
    const stepsObj = filePreds.value?.[f];
    if (!stepsObj) return;

    stepsObj.pred = stepsObj?.pred.filter(
      (el) => !missedStepsFromTelemetry.value?.includes(el)
    );

    const tmp = { ...stepsTime.value };
    const { pred, times } = stepsObj;

    // Background class
    if (pred[0] === taskStepsCount.value) {
      backgroundTime.value = times[0] || 0;
      return;
    }

    cycleTime.value += times[0] - (stepsTime.value?.[pred[0]] || 0);
    pred.forEach((step, idx) => {
      tmp[step] = times[idx];
    });
    stepsTime.value = tmp;
  }

  function precomputeAllSubstepMetrics() {
    if (!filePreds.value) return;
    if (localStorage.getItem('organization') !== 'bmwgermanye5cmzyxzt6bi9rso6') return;

    const totalFrames = Object.keys(filePreds.value).length;

    // --- Pass 1: determine which step each background frame is attributed to ---
    const bgAttribution = {}; // frame → stepIndex to receive NVA
    let lastSeenStep = null;

    // Forward scan: find the next valid step for each BG frame
    const pendingBgFrames = [];
    for (let f = 0; f < totalFrames; f++) {
      const frameData = filePreds.value[f];
      if (!frameData) continue;

      const validSteps = frameData.pred.filter(
        (s) =>
          s !== 0 &&
          s !== taskStepsCount.value &&
          !missedStepsFromTelemetry.value?.includes(s)
      );

      const isBg =
        validSteps.length === 0 &&
        frameData.pred.includes(taskStepsCount.value);

      if (isBg) {
        pendingBgFrames.push(f);
      } else if (validSteps.length > 0) {
        // Flush all pending BG frames → this step
        const target = validSteps[0];
        pendingBgFrames.forEach((bf) => { bgAttribution[bf] = target; });
        pendingBgFrames.length = 0;
        lastSeenStep = target;
      }
    }
    // Trailing BG frames → attribute to the last task step seen
    if (pendingBgFrames.length > 0 && lastSeenStep !== null) {
      pendingBgFrames.forEach((bf) => { bgAttribution[bf] = lastSeenStep; });
    }

    // --- Pass 2: accumulate VA & NVA frame-by-frame for smooth animation ---
    const lookup = {};
    const vaAccum = {};
    const nvaAccum = {};

    for (let f = 0; f < totalFrames; f++) {
      const frameData = filePreds.value[f];
      if (!frameData) {
        lookup[f] = f > 0 ? lookup[f - 1] : {};
        continue;
      }

      const fps = frameData.fps || 30;
      const frameDuration = 1 / fps;

      if (bgAttribution[f] !== undefined) {
        // Background frame — increment NVA for its attributed step
        const target = bgAttribution[f];
        nvaAccum[target] = (nvaAccum[target] || 0) + frameDuration;
      } else {
        // Task step frame — increment VA for all valid steps
        const validSteps = frameData.pred.filter(
          (s) =>
            s !== 0 &&
            s !== taskStepsCount.value &&
            !missedStepsFromTelemetry.value?.includes(s)
        );
        validSteps.forEach((stepIndex) => {
          vaAccum[stepIndex] = (vaAccum[stepIndex] || 0) + frameDuration;
        });
      }

      // Snapshot current cumulative metrics for this frame
      const snapshot = {};
      const allSteps = new Set([
        ...Object.keys(vaAccum),
        ...Object.keys(nvaAccum),
      ]);
      allSteps.forEach((stepIndex) => {
        const va = vaAccum[stepIndex] || 0;
        const nva = nvaAccum[stepIndex] || 0;
        snapshot[stepIndex] = { vaTime: va, nvaTime: nva, totalTime: va + nva };
      });

      lookup[f] = snapshot;
    }

    precomputedMetrics.value = lookup;
  }

  function buildMetaSnapshots() {
    metaSnapshots.value = [];
    metaKeys.value = [];
    if (!filePreds.value) return;

    const frameKeys = Object.keys(filePreds.value)
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);

    let cumulative = {};
    const seen = new Set();
    const snaps = [];
    for (const f of frameKeys) {
      const m = filePreds.value[f]?.meta;
      if (!m || typeof m !== 'object') continue;
      cumulative = { ...cumulative, ...m };
      Object.keys(m).forEach((k) => seen.add(k));
      snaps.push({ frame: f, state: cumulative });
    }
    metaSnapshots.value = snaps;
    metaKeys.value = Array.from(seen);
  }

  // Video sync
  function highlightCurrentStep() {
    try {
      const current = videoEl.value?.currentTime?.toFixed(5);
      currentVideoTime.value = current;
      const num = (Number(current) || 0) * (props?.video?.fps || 30);
      frame.value = Math.floor(num);
    } catch (e) {
      resetState();
    }
  }

  function onLoad() {
    showVideoControls.value = true;
    boundingBoxesEl.value?.setupCanvas?.();
  }

  function onPlay() {
    const loop = () => {
      highlightCurrentStep();
      boundingBoxesEl.value?.drawBoundingBoxes?.();
      animationFrameId.value = requestAnimationFrame(loop);
    };
    animationFrameId.value = requestAnimationFrame(loop);
  }

  function onPause() {
    cancelAnimationFrame(animationFrameId.value);
  }

  function onEnd() {
    cancelAnimationFrame(animationFrameId.value);
  }

  function handleSeeking(e) {
    const current = videoEl.value?.currentTime?.toFixed(5);
    currentVideoTime.value = current;
    if (isPredictionFileNotExist.value) return;

    setStepsStartTime();
    cycleTime.value = 0;

    const fps = props?.video?.fps || 30;
    const currentFrame = Math.floor((e?.target?.currentTime || 0) * fps);
    frame.value = currentFrame;
    for (let i = 0; i <= currentFrame; i++) {
      updateSubstepTimes(i);
    }
    boundingBoxesEl.value?.drawBoundingBoxes?.(currentFrame);
  }

  function skipTo(timeInSec) {
    if (videoEl.value) videoEl.value.currentTime = timeInSec;
  }

  // Data fetching
  async function handleGetTraceRecordData(query) {
    if (props?.video?.cycleData) {
      traceCycleData.value = props.video.cycleData;
      isGoodCycle.value = props.video.cycleData?.is_good_cycle;
      return;
    }
    const [error, data] = await TelemetryService.getTraceCycleRecordData(query);
    if (error) return;

    traceCycleData.value = data.cycle;
    isGoodCycle.value = data?.cycle?.is_good_cycle;
    if (data?.cycle?.history_id) historyId.value = data.cycle.history_id;
    if (data?.task_record?.Task) taskId.value = data?.task_record?.Task;
  }

  async function handleFetchRequiredData() {
    const { traceId } = route.params;
    const { cycle_id } = route.query;
    let query;
    if (traceId && cycle_id) {
      query = { cycle_id, task_record_id: traceId };
    } else {
      query = {
        cycle_id: props?.video?.cycle_id,
        task_record_id: props?.video?.id,
      };
    }

    if (query.cycle_id && query.task_record_id) {
      await handleGetTraceRecordData(query);
      await store.dispatch('fetchTaskDetails', {
        taskId: Number(taskId.value),
        historyId: historyId.value,
      });
    }

    await updatePredictions();
    updateMissedSteps();
  }

  function updateMissedSteps() {
    if (!props?.video || isGoodCycle.value) return;
    missedSteps.value = props.video.missedStepsName;
  }

  async function updatePredictions() {
    if (!props?.video) return;
    const { per_frame_prediction_file_url } = props.video;
    try {
      const { data } = await axios.get(per_frame_prediction_file_url);
      const { preds, propagated_steps } = data;
      if (!preds || Object.keys(preds).length === 0) {
        console.error('[preds] downloaded but no usable preds', typeof data);
        isPredictionFileNotExist.value = true;
        filePreds.value = null;
        return;
      }
      const firstFrame = preds[Object.keys(preds)[0]];
      emit?.('showVisualization', !!(firstFrame && firstFrame['bboxes']));
      setStepStartPoint(preds);
      propagatedSteps.value = propagated_steps || [];
      filePreds.value = preds;
      precomputeAllSubstepMetrics();
      buildMetaSnapshots();
    } catch (e) {
      console.error('[preds] fetch/parse failed', e?.response?.status, e?.message);
      isPredictionFileNotExist.value = true;
      filePreds.value = null;
      detectedSteps.value = {};
      metaSnapshots.value = [];
      metaKeys.value = [];
    }
  }

  async function toggleCycleStatus() {
    const val = !traceCycleData.value?.is_good_cycle;
    if (traceCycleData.value) traceCycleData.value.is_good_cycle = val;
    isGoodCycle.value = val;
    updateMissedSteps();
    emit?.('toggleCycleStatus', { video: props.video, isGoodCycle: val });
  }

  async function archiveCycle(video) {
    await TelemetryService.archiveCycle(video.cycle_id);
    emit?.('archivedCycleCount', video.is_good_cycle);
    if (props?.navigate) router.push('/');
  }

  function handleClickSubStep(stepIndex) {
    if (!(stepIndex in stepsStartPoints.value)) return;
    const f = stepsStartPoints.value[stepIndex];
    const fps = props?.video?.fps || 30;
    skipTo(f / fps);
  }

  // Wire refs from host
  function attachVideo(el) {
    console.log('attach video', el);
    videoEl.value = el;
  }

  function attachBoundingBoxes(el) {
    boundingBoxesEl.value = el;
  }

  function resetState() {
    frame.value = 0;
    currentStepList.value = [];
    currentCycle.value = 1;
    detectedSteps.value = {};
    isPredictionFileNotExist.value = false;
    stepsTime.value = {};
    substepMetrics.value = {};
    precomputedMetrics.value = {};
    metaSnapshots.value = [];
    metaKeys.value = [];
    cancelAnimationFrame(animationFrameId.value);
  }

  // Watches
  watch(
    () => props?.video,
    (nv, ov) => {
      if (nv?.cycle_id !== ov?.cycle_id) {
        handleFetchRequiredData();

        const steps = nv?.missed_steps?.split(', ');
        if (steps?.length) {
          missedStepsFromTelemetry.value = steps.map((s) =>
            s ? Number(s) : ''
          );
        }
      }
      updateMissedSteps();
    },
    { deep: true }
  );

  watch(frame, (currentFrame) => {
    currentStepList.value =
      filePreds.value?.[currentFrame]?.pred?.filter(
        (el) => !missedStepsFromTelemetry.value?.includes(el)
      ) || [];
    updateSubstepTimes();
    substepMetrics.value = precomputedMetrics.value[currentFrame] || {};
  });

  watch(
    () => props?.stepsList,
    (list) => {
      const onlySub = (list || []).filter((el) => el.type === 'sub-step');
      substepList.value = onlySub;
    },
    { deep: true, immediate: true }
  );

  watch(currentStepList, (list) => list.forEach(addStepInDetectedStepsDict));
  watch(currentCycle, () => {
    detectedSteps.value = {};
    stepsTime.value = {};
    substepMetrics.value = {};
    precomputedMetrics.value = {};
  });

  // Mount
  onMounted(() => {
    const temp = (props?.stepsList || []).filter(
      (el) => el.type === 'sub-step'
    );
    substepList.value = temp;
    setStepsStartTime(temp);
    handleFetchRequiredData();
  });

  onBeforeUnmount(() => {
    resetState();
  });

  return {
    // DOM refs hookups
    videoEl,
    boundingBoxesEl,
    attachVideo,
    attachBoundingBoxes,

    // Store-derived
    negativeSteps,
    optionalSteps,
    isFetchingTaskDetails,
    stepsTranslationMapping,
    role,

    // State
    isGoodCycle,
    frame,
    currentStepList,
    currentCycle,
    filePreds,
    stepsStartPoints,
    stepsTime,
    substepList,
    backgroundTime,
    missedSteps,
    detectedSteps,
    isPredictionFileNotExist,
    processDetails,
    showVideoControls,
    propagatedSteps,
    traceCycleData,
    currentVideoTime,
    cycleTime,
    missedStepsFromTelemetry,
    currentStepsText,
    nextStepText,
    potentialIssuesText,
    taskStepsCount,
    stepsCompleted,
    substepMetrics,
    stationMeta,
    currentFps,

    // Computed
    cycleType,
    isMetaExist,

    // Actions
    setStepsStartTime,
    setStepStartPoint,
    updateSubstepTimes,
    addStepInDetectedStepsDict,
    onLoad,
    onPlay,
    onPause,
    onEnd,
    handleSeeking,
    highlightCurrentStep,
    skipTo,
    handleFetchRequiredData,
    handleGetTraceRecordData,
    updateMissedSteps,
    updatePredictions,
    toggleCycleStatus,
    archiveCycle,
    handleClickSubStep,
    resetState,
  };
}
